import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Users, Image, Stethoscope, Sparkles } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudySouthernHealth: React.FC = () => {
  const navigationStore = useNavigationStore()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)
  const handleBookDemo = () => navigationStore.openDemoModal()

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How Southern Health & Social Care Trust Accelerated Dermatology Access with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">ReferralCX and eConsultCX work together to centralize dermatology referrals, insert specialist review upstream, and reduce unnecessary in-person visits.</p>
              <div className="mt-8 flex gap-3">
                <button onClick={() => setDownloadFormOpen(true)} className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition">Download Full Case Study</button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">econsult</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">referral</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">ReferralCX manages intake and routing, while eConsultCX supports dermatology triage and specialist plans.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Faster access</div>
              <div className="text-slate-600">Patients seen sooner, closer to home</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Image className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Image-assisted triage</div>
              <div className="text-slate-600">Photos with history inform decisions</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Stethoscope className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Launched in 2008</div>
              <div className="text-slate-600">ReferralCX + eConsultCX in continual use</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">More patients, same resources</div>
              <div className="text-slate-600">Capacity unlocked by remote review</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">High dermatology referral volume and long waits needed a better triage model—ensuring only patients who truly need in-person care are scheduled.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Limited clinic capacity and time-consuming first visits</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Inconsistent referral information and lack of images</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Long waits for benign or self-care cases</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Clinic bottlenecks</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Variable referral quality</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Avoidable first visits</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Patient anxiety</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">With <strong>ReferralCX</strong> and <strong>eConsultCX</strong>, GPs submit dermatology history, targeted questions, and photos. ReferralCX manages intake and routing, while eConsultCX enables asynchronous specialist review, documentation of a plan, and conversion to a clinic visit when needed.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Rich clinical intake</div><div className="mt-1 text-slate-600">History + images + Q&A from referrer/patient</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Specialist review</div><div className="mt-1 text-slate-600">Plan, advice, or convert to clinic visit</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">End-to-end tracking</div><div className="mt-1 text-slate-600">From submission to diagnosis/report</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Knowledge transfer</div><div className="mt-1 text-slate-600">Guidance improves GP dermatology care</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Faster access</strong> with remote specialist review and clear next steps.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Fewer unnecessary visits</strong>—clinic time reserved for patients who truly need it.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>More patients, same resources</strong> by shifting triage upstream via ReferralCX + eConsultCX.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Patients avoid travel and long waits; GPs get rapid feedback; dermatology teams focus on complex cases.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Care closer to home with timely reassurance</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Shared plans improve continuity and outcomes</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“Eceptionist worked with us to understand our workflow… we are able to provide patients with better service while ensuring patients that need to be seen quickly are.”</blockquote>
            <div className="mt-3 text-slate-600">— Senior Dermatology Nurse, Southern Health & Social Care Trust</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">In place since 2008, ReferralCX + eConsultCX have become a durable front-door for dermatology—streamlining intake, accelerating decisions, and freeing clinic capacity.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Proven longevity</div><div className="text-slate-600 mt-1">Operational for 15+ years</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Better first decisions</div><div className="text-slate-600 mt-1">Images + history improve triage</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Capacity unlocked</div><div className="text-slate-600 mt-1">Prioritize in-person for complex cases</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Want faster specialty access without more clinics?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can triage upstream and reserve in-person time for what matters.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Southern Health & Social Care Trust Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Case+Study_Southern+Health.pdf"
      />
    </main>
  )
}

export default CaseStudySouthernHealth


